import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  IconCalendar, 
  IconSchool, 
  IconUserPlus, 
  IconBuilding, 
  IconMapPin, 
  IconUsers, 
  IconDeviceMobile,
  IconSearch,
  IconHeart,
  IconBrandFacebook,
  IconBrandTwitter,
  IconBrandInstagram,
  IconStar,
  IconFilter,
  IconMap,
  IconArrowDown,
  IconMoodHappy,
  IconConfetti,
  IconBulb,
  IconRocket
} from '@tabler/icons-react';
import styles from './LandingPage.module.css';
import { ROUTES } from '../constants/routes';
import { useAuth } from '../auth/hooks/useAuth';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated: isLoggedIn, user } = useAuth();
  const [activeTab, setActiveTab] = useState('students');

  // Rediriger les utilisateurs déjà connectés vers la page appropriée
  useEffect(() => {
    if (isLoggedIn && user) {
      if (user.role === 'org_admin') {
        navigate('/org');
      } else {
        navigate('/events');
      }
    }
  }, [isLoggedIn, user, navigate]);

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.logo}>
          {/* Remplacement du titre textuel par le logo en accès public direct */}
          <img src="/logo_etudown_title.png" alt="Etudown" style={{ maxHeight: 48, width: 'auto' }} />
        </div>
        <div className={styles.nav}>
          <button className={styles.loginButton} onClick={() => navigate(ROUTES.LOGIN)}>
            Se connecter
          </button>
          <button className={styles.signupButton} onClick={() => navigate(ROUTES.SIGNUP)}>
            S'inscrire
          </button>
        </div>
      </header>

      <main>
        <section className={styles.hero}>
          <div className={styles.heroContent}>
            <div className={styles.badge}>
              <span>Nouveau</span> Événements à proximité 🎉
            </div>
            <h1>
              <span className={styles.gradientText}>Découvre</span> tout ce qui bouge sur ton campus !
            </h1>
            <p>
              EtuDown, c'est LA plateforme qui te connecte aux événements qui te ressemblent.
              Concerts, soirées, conférences, ateliers... Ne manque plus rien !
            </p>
            <div className={styles.heroCta}>
              <button 
                className={styles.ctaButton}
                onClick={() => navigate(ROUTES.SIGNUP)}
              >
                Rejoindre gratuitement
              </button>
              <button 
                className={styles.secondaryButton}
                onClick={() => {
                  document.getElementById('discover')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Découvrir <IconArrowDown size={16} />
              </button>
            </div>
            <div className={styles.shapes}>
              <div className={styles.shape1}></div>
              <div className={styles.shape2}></div>
              <div className={styles.shape3}></div>
            </div>
          </div>
          <div className={styles.heroImage}>
            <div className={styles.imagePlaceholder}>
              <div className={styles.eventCard} style={{ backgroundColor: '#ffcdd2' }}>
                <div className={styles.eventCategory} style={{ backgroundColor: '#f44336', color: 'white' }}>Soirée</div>
                <h3>Soirée d'intégration 2025</h3>
                <p><IconMapPin size={14} /> Campus Central • <IconCalendar size={14} /> 20h00</p>
                <div className={styles.distance}>À 1.2 km</div>
              </div>
              <div className={styles.eventCard} style={{ top: '60px', left: '100px', backgroundColor: '#e1f5fe' }}>
                <div className={styles.eventCategory} style={{ backgroundColor: '#03a9f4', color: 'white' }}>Carrière</div>
                <h3>Atelier CV & Entretiens</h3>
                <p><IconMapPin size={14} /> Bibliothèque Universitaire • <IconCalendar size={14} /> 14h00</p>
                <div className={styles.distance}>À 800 m</div>
              </div>
              <div className={styles.eventCard} style={{ top: '130px', left: '30px', backgroundColor: '#f0f4c3' }}>
                <div className={styles.eventCategory} style={{ backgroundColor: '#cddc39', color: '#333' }}>Loisirs</div>
                <h3>Tournament e-Sport</h3>
                <p><IconMapPin size={14} /> Salle Multimédia • <IconCalendar size={14} /> 18h00</p>
                <div className={styles.distance}>À 1.5 km</div>
              </div>
              <div className={styles.phoneMockup}></div>
            </div>
          </div>
        </section>

        <section id="discover" className={styles.benefits}>
          <h2>Pourquoi c'est <span className={styles.gradientText}>génial</span> ?</h2>
          <p className={styles.sectionSubtitle}>Tout ce que tu peux faire avec EtuDown</p>
          
          <div className={styles.tabsContainer}>
            <div className={styles.tabs}>
              <button 
                className={`${styles.tab} ${activeTab === 'students' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('students')}
              >
                <IconSchool size={20} /> Pour toi
              </button>
              <button 
                className={`${styles.tab} ${activeTab === 'organizations' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('organizations')}
              >
                <IconBuilding size={20} /> Pour ton asso
              </button>
              <button 
                className={`${styles.tab} ${activeTab === 'casual' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('casual')}
              >
                <IconUsers size={20} /> Pour tous
              </button>
            </div>
            
            <div className={styles.tabContent}>
              {activeTab === 'students' && (
                <div className={styles.tabPanel}>
                  <div className={styles.tabFeatures}>
                    <div className={styles.tabFeature} style={{ backgroundColor: '#e3f2fd' }}>
                      <div className={styles.tabFeatureIcon} style={{ backgroundColor: 'rgba(33, 150, 243, 0.2)', color: '#1976d2' }}><IconSearch size={28} /></div>
                      <h3>Trouve ton style d'event</h3>
                      <p>Filtres par intérêts, distance, date... c'est toi qui choisis !</p>
                    </div>
                    <div className={styles.tabFeature} style={{ backgroundColor: '#fff8e1' }}>
                      <div className={styles.tabFeatureIcon} style={{ backgroundColor: 'rgba(255, 193, 7, 0.2)', color: '#f57f17' }}><IconHeart size={28} /></div>
                      <h3>Garde tes events préférés</h3>
                      <p>Enregistre ce qui t'intéresse et reçois des rappels pour ne rien manquer.</p>
                    </div>
                    <div className={styles.tabFeature} style={{ backgroundColor: '#f3e5f5' }}>
                      <div className={styles.tabFeatureIcon} style={{ backgroundColor: 'rgba(156, 39, 176, 0.2)', color: '#7b1fa2' }}><IconMap size={28} /></div>
                      <h3>Navigue facilement</h3>
                      <p>La carte te montre tous les events près de toi, avec les distances exactes.</p>
                    </div>
                    <div className={styles.tabFeature} style={{ backgroundColor: '#e8f5e9' }}>
                      <div className={styles.tabFeatureIcon} style={{ backgroundColor: 'rgba(76, 175, 80, 0.2)', color: '#388e3c' }}><IconDeviceMobile size={28} /></div>
                      <h3>Accessible partout</h3>
                      <p>Sur ton téléphone, ta tablette ou ton ordi, EtuDown te suit où que tu sois.</p>
                    </div>
                  </div>
                  <div className={styles.tabImage}>
                    <div className={styles.studentImage}></div>
                  </div>
                  <div className={styles.tabAction}>
                    <button 
                      className={styles.actionButton}
                      onClick={() => navigate(ROUTES.SIGNUP)}
                    >
                      <IconConfetti size={18} /> Créer un profil
                    </button>
                  </div>
                </div>
              )}
              
              {activeTab === 'organizations' && (
                <div className={styles.tabPanel}>
                  <div className={styles.tabFeatures}>
                    <div className={styles.tabFeature} style={{ backgroundColor: '#e0f7fa' }}>
                      <div className={styles.tabFeatureIcon} style={{ backgroundColor: 'rgba(0, 188, 212, 0.2)', color: '#0097a7' }}><IconCalendar size={28} /></div>
                      <h3>Gestion ultra simple</h3>
                      <p>Créez et gérez vos events en quelques clics depuis un tableau de bord intuitif.</p>
                    </div>
                    <div className={styles.tabFeature} style={{ backgroundColor: '#fff3e0' }}>
                      <div className={styles.tabFeatureIcon} style={{ backgroundColor: 'rgba(255, 152, 0, 0.2)', color: '#ef6c00' }}><IconUsers size={28} /></div>
                      <h3>Trouvez des bénévoles</h3>
                      <p>Recrutez facilement des staffs motivés pour vos événements.</p>
                    </div>
                    <div className={styles.tabFeature} style={{ backgroundColor: '#f1f8e9' }}>
                      <div className={styles.tabFeatureIcon} style={{ backgroundColor: 'rgba(139, 195, 74, 0.2)', color: '#689f38' }}><IconStar size={28} /></div>
                      <h3>Gagnez en visibilité</h3>
                      <p>Faites connaître votre asso et vos events auprès de tous les étudiants.</p>
                    </div>
                    <div className={styles.tabFeature} style={{ backgroundColor: '#e8eaf6' }}>
                      <div className={styles.tabFeatureIcon} style={{ backgroundColor: 'rgba(63, 81, 181, 0.2)', color: '#3949ab' }}><IconFilter size={28} /></div>
                      <h3>Suivez vos performances</h3>
                      <p>Analysez la participation et améliorez vos prochains events.</p>
                    </div>
                  </div>
                  <div className={styles.tabImage}>
                    <div className={styles.orgImage}></div>
                  </div>
                  <div className={styles.tabAction}>
                    <button 
                      className={styles.actionButton}
                      onClick={() => navigate(ROUTES.SIGNUP)}
                    >
                      <IconRocket size={18} /> Lancer mon organisation
                    </button>
                  </div>
                </div>
              )}
              
              {activeTab === 'casual' && (
                <div className={styles.tabPanel}>
                  <div className={styles.tabFeatures}>
                    <div className={styles.tabFeature} style={{ backgroundColor: '#ffebee' }}>
                      <div className={styles.tabFeatureIcon} style={{ backgroundColor: 'rgba(244, 67, 54, 0.2)', color: '#d32f2f' }}><IconMapPin size={28} /></div>
                      <h3>Tout près de toi</h3>
                      <p>Découvre les activités à deux pas grâce à la géolocalisation ultra précise.</p>
                    </div>
                    <div className={styles.tabFeature} style={{ backgroundColor: '#e3f2fd' }}>
                      <div className={styles.tabFeatureIcon} style={{ backgroundColor: 'rgba(33, 150, 243, 0.2)', color: '#1976d2' }}><IconSearch size={28} /></div>
                      <h3>Sans inscription</h3>
                      <p>Parcours les events publics sans même créer de compte. Simple et rapide !</p>
                    </div>
                    <div className={styles.tabFeature} style={{ backgroundColor: '#fce4ec' }}>
                      <div className={styles.tabFeatureIcon} style={{ backgroundColor: 'rgba(233, 30, 99, 0.2)', color: '#c2185b' }}><IconCalendar size={28} /></div>
                      <h3>Agenda complet</h3>
                      <p>Accède à tous les events étudiants de ta région en un coup d'œil.</p>
                    </div>
                    <div className={styles.tabFeature} style={{ backgroundColor: '#f9fbe7' }}>
                      <div className={styles.tabFeatureIcon} style={{ backgroundColor: 'rgba(205, 220, 57, 0.2)', color: '#afb42b' }}><IconMoodHappy size={28} /></div>
                      <h3>Personnalisé pour toi</h3>
                      <p>Crée un compte pour recevoir des suggestions qui te correspondent vraiment.</p>
                    </div>
                  </div>
                  <div className={styles.tabImage}>
                    <div className={styles.casualImage}></div>
                  </div>
                  <div className={styles.tabAction}>
                    <button 
                      className={styles.actionButton}
                      onClick={() => navigate(ROUTES.HOME)}
                    >
                      <IconBulb size={18} /> Explorer maintenant
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className={styles.features}>
          <h2>Comment ça <span className={styles.gradientText}>marche</span> ?</h2>
          <p className={styles.sectionSubtitle}>Simple comme bonjour !</p>
          
          <div className={styles.featureGrid}>
            <div className={styles.featureCard}>
              <div className={styles.featureIcon} style={{ background: 'linear-gradient(135deg, #ff9a9e, #fad0c4)' }}>
                <IconUserPlus color="#fff" size={36} />
                <div className={styles.stepNumber}>1</div>
              </div>
              <h3>Crée ton profil</h3>
              <p>Inscris-toi en quelques secondes et personnalise ton profil selon tes passions.</p>
            </div>
            
            <div className={styles.featureCard}>
              <div className={styles.featureIcon} style={{ background: 'linear-gradient(135deg, #a1c4fd, #c2e9fb)' }}>
                <IconCalendar color="#fff" size={36} />
                <div className={styles.stepNumber}>2</div>
              </div>
              <h3>Découvre des events</h3>
              <p>Parcours tous les événements organisés par les associations et ton établissement.</p>
            </div>
            
            <div className={styles.featureCard}>
              <div className={styles.featureIcon} style={{ background: 'linear-gradient(135deg, #d4fc79, #96e6a1)' }}>
                <IconMapPin color="#fff" size={36} />
                <div className={styles.stepNumber}>3</div>
              </div>
              <h3>À deux pas de toi</h3>
              <p>Trouve des events à proximité grâce à notre système de géolocalisation précis.</p>
            </div>
            
            <div className={styles.featureCard}>
              <div className={styles.featureIcon} style={{ background: 'linear-gradient(135deg, #fbc2eb, #a6c1ee)' }}>
                <IconBuilding color="#fff" size={36} />
                <div className={styles.stepNumber}>4</div>
              </div>
              <h3>Pour les assos aussi</h3>
              <p>Les organisations étudiantes peuvent créer et gérer leurs événements facilement.</p>
            </div>
          </div>
        </section>

        <section className={styles.cta}>
          <div className={styles.ctaParticles}>
            <div className={styles.particle1}></div>
            <div className={styles.particle2}></div>
            <div className={styles.particle3}></div>
            <div className={styles.particle4}></div>
            <div className={styles.particle5}></div>
          </div>
          <h2>Prêt à rejoindre <span className={styles.ctaHighlight}>la communauté</span> ?</h2>
          <p>Ne manque plus aucun événement sur ton campus. C'est gratuit et ça le restera !</p>
          <button 
            className={styles.ctaButton}
            onClick={() => navigate(ROUTES.SIGNUP)}
          >
            S'inscrire gratuitement
          </button>
          <p className={styles.loginLink}>
            Déjà membre ? <span onClick={() => navigate(ROUTES.LOGIN)}>Se connecter</span>
          </p>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerLogo}>
            <img src="/logo_etudown_title.png" alt="Etudown" style={{ maxHeight: 48, width: 'auto', marginBottom: 8 }} />
            <p>La plateforme des événements étudiants</p>
            <div className={styles.socialLinks}>
              <a href="#" className={styles.socialIcon}><IconBrandFacebook size={20} /></a>
              <a href="#" className={styles.socialIcon}><IconBrandTwitter size={20} /></a>
              <a href="#" className={styles.socialIcon}><IconBrandInstagram size={20} /></a>
            </div>
          </div>
          <div className={styles.footerLinks}>
            <div className={styles.footerColumn}>
              <h3>À propos</h3>
              <ul>
                <li>Notre mission</li>
                <li>Équipe</li>
                <li>Nous contacter</li>
              </ul>
            </div>
            <div className={styles.footerColumn}>
              <h3>Légal</h3>
              <ul>
                <li>Conditions d'utilisation</li>
                <li>Politique de confidentialité</li>
                <li>Mentions légales</li>
              </ul>
            </div>
            <div className={styles.footerColumn}>
              <h3>Ressources</h3>
              <ul>
                <li>Centre d'aide</li>
                <li>Guide d'utilisation</li>
                <li>FAQ</li>
              </ul>
            </div>
          </div>
        </div>
        <div className={styles.copyright}>
          &copy; {new Date().getFullYear()} EtuDown - Tous droits réservés
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
